import { type OptionItemDescription } from "@common/declarations";
import withDynamicOptionsHOC, { type WithDynamicOptionsAddedProps } from "@common/hocs/withDynamicOptions";
import { type WithLazyHOCProps, withLazyHOC } from "@common/hocs/withLazy";
import { sort } from "@common/lib/array";
import { Common } from "@common/types";
import classnames from "classnames";
import { isArray, uniq } from "lodash";
import React from "react";
import withAddHOC, { type WithAddHOCBaseProps } from "../hocs/withAdd";
import withSearchTextHOC, { withSearchHOC } from "../hocs/withSearch";
import withStaticHOC, { type LazySelectComponentProps } from "../hocs/withStatic";
import { optionItemComparer } from "../lib";
import {
  MultiSelectBaseLazy,
  type MultiSelectBaseProps,
  type MultiSelectGroupType,
  type MultiSelectOptionItemBase
} from "./Base/component";
import { type ValueType, getBubbledOptions, toOutputValue, valueToArray } from "./lib";

export type MultiSelectValue<T = ValueType> = MultiSelectOptionItem<T>[] | T[] | string;
export type MultiSelectOptionItem<T> = Common.LabelValue<T> & {
  descriptions?: OptionItemDescription[];
  group?: string;
  isDisabled?: boolean;
  tourStep?: string;
};

export type MultiSelectOutputType = "array" | "option" | "string" | "value";

// value can be an array or a string means a CSV, then onChange will receive a CSV as well
export type MultiSelectProps = Omit<
  MultiSelectBaseProps<ValueType>,
  "nullIndicator" | "onChange" | "options" | "selected" | "value"
> &
  Common.WithValue<MultiSelectValue> &
  Common.WithOnChange<MultiSelectValue> &
  Common.WithOptions<MultiSelectOptionItem<ValueType>> & {
    allOptions: MultiSelectOptionItem<ValueType>[];
    bubbleSelected?: boolean;
    excludeOptionValues?: ValueType[];
    groupType?: MultiSelectGroupType;
    hideSelectAll?: boolean;
    outputType?: MultiSelectOutputType;
    /** mutally-exclusive values. Only one can be selected at a time */
    uniqueValues?: ValueType[];
  };

export type MultiSelectLazyProps = MultiSelectProps & LazySelectComponentProps & {};

const MultiSelectLazyBase = ({
  allOptions,
  excludeOptionValues,
  hideSelectAll,
  groupType,
  onChange,
  options,
  outputType,
  value,
  uniqueValues,
  ...props
}: MultiSelectLazyProps) => {
  const [initialOptions, setInitialOptions] = React.useState<MultiSelectOptionItem<ValueType>[]>([]);
  const innerOptions = React.useMemo(
    () => options.filter((x) => !(excludeOptionValues ?? []).includes(x.value)),
    [options, excludeOptionValues]
  );

  React.useEffect(() => setInitialOptions(innerOptions), [innerOptions]);

  const _finalOptions = React.useMemo((): MultiSelectOptionItemBase<ValueType>[] => {
    const selected = valueToArray(initialOptions, value);

    const mapItems = (opts: MultiSelectOptionItem<ValueType>[]): MultiSelectOptionItemBase<ValueType>[] => {
      const result = opts.map<MultiSelectOptionItemBase<ValueType>>((x) => ({
        tourStep: x.tourStep,
        descriptions: x.descriptions,
        group: x.group,
        isDisabled: x.isDisabled,
        isSelected: selected.some((v) => v.value == x.value),
        label: x.label,
        value: x.value
      }));

      return props.sort && !props.bubbleSelected ? sort(result, { key: "label", order: props.sort }) : result;
    };

    const groups = uniq(initialOptions.map((x) => x.group));
    if (groups.length === 1 && !groups[0]) {
      return mapItems(initialOptions);
    }

    const groupped = initialOptions.reduce((res, x) => {
      const group = x.group ?? "";
      if (!(group in res)) {
        res[group] = [];
      }
      res[group].push(x);
      return res;
    }, {});

    return Object.keys(groupped).reduce((res, g) => {
      if (!g) {
        return [...res, ...mapItems(groupped[g])];
      }

      const children = mapItems(groupped[g]);
      if (groupType === "parent") {
        const selectedCount = children.filter((x) => x.isSelected).length;
        return [
          ...res,
          {
            label: g,
            value: g,
            children: children.map(({ group, ...x }) => x), // exclude group
            isSelected: selectedCount === children.length ? true : !selectedCount ? false : null,
            type: groupType
          }
        ];
      }

      return [...res, ...children];
    }, []);
  }, [excludeOptionValues, initialOptions, groupType, props.bubbleSelected, props.sort, value]);

  const _onOpen = React.useCallback(() => {
    let sortedOptions: MultiSelectOptionItem<ValueType>[] = [];
    if (props.bubbleSelected) {
      sortedOptions = getBubbledOptions(
        options,
        valueToArray(options, value).map((x) => x.value),
        props.sort,
        excludeOptionValues
      );
    } else {
      sortedOptions = props.sort
        ? sort(innerOptions, { key: "label", order: props.sort, type: "natural" })
        : innerOptions;
    }

    setInitialOptions(sortedOptions);
    props.onOpen && props.onOpen();
  }, [excludeOptionValues, options, innerOptions, value, props.sort, props.bubbleSelected, props.onOpen]);

  const _onClose = React.useCallback(() => {
    setInitialOptions(innerOptions);
    props.onClose && props.onClose();
  }, [innerOptions, props.onClose]);

  const _onChange = React.useCallback(
    (opt: MultiSelectOptionItemBase<ValueType>, isChecked: boolean) => {
      let newValue = valueToArray(innerOptions, value).slice();
      if (opt.children) {
        const groupValues = innerOptions.filter((x) => x.group == opt.value);
        if (isChecked) {
          newValue = [...newValue, ...groupValues];
        } else {
          newValue = newValue.filter((x) => !groupValues.some((y) => x == y));
        }
      } else {
        if (isChecked) {
          newValue.splice(0, 0, opt);
        } else {
          const index = newValue.findIndex((x) => x.value == opt.value);
          newValue.splice(index, 1);
        }
      }

      onChange(toOutputValue(newValue, { initialValue: value, allOptions, options, outputType, uniqueValues }));
    },
    [allOptions, onChange, innerOptions, outputType, value, uniqueValues]
  );

  const onSelectAllClick = React.useCallback(
    (isChecked: boolean) =>
      onChange(
        toOutputValue(isChecked ? innerOptions : [], {
          initialValue: value,
          allOptions,
          options,
          outputType,
          uniqueValues
        })
      ),
    [allOptions, onChange, groupType, innerOptions, options, outputType, value, uniqueValues]
  );

  return (
    <MultiSelectBaseLazy
      {...props}
      onOpen={_onOpen}
      onClose={_onClose}
      sort={props.bubbleSelected ? undefined : props.sort}
      allOptions={allOptions}
      nullIndicator="indeterminated"
      options={_finalOptions}
      onChange={_onChange}
      onSelectAllClick={hideSelectAll ? undefined : onSelectAllClick}
      selected={valueToArray(innerOptions, value).length}
    />
  );
};
export const MultiSelectLazy = withLazyHOC(MultiSelectLazyBase, optionItemComparer);
export const MultiSelectLazyWithSearch = withSearchHOC(MultiSelectLazy);
const MultiSelectLazyWithAddBase = withAddHOC(MultiSelectLazyWithSearch);

export const MultiSelectLazyWithAdd = ({
  dropdownClassName,
  ...props
}: Omit<
  MultiSelectLazyProps & WithLazyHOCProps<any> & WithAddHOCBaseProps,
  "options" | "hasMore" | "loadMore" | "bubbleSelected"
>) => <MultiSelectLazyWithAddBase {...props} dropdownClassName={classnames(dropdownClassName, "with_add")} />;

const MultiSelectStatic = withStaticHOC(MultiSelectLazyBase);

export const MultiSelect = withSearchHOC(
  withSearchTextHOC(({ alwaysShowHeading, heading, ...props }: MultiSelectProps & { alwaysShowHeading?: boolean }) => {
    const _heading = React.useMemo(() => {
      if (!heading) return;

      let result = isArray(heading) ? heading : [heading];
      if (props.allOptions?.length < 10 && !alwaysShowHeading) {
        result = result.filter((x) => !x.isSearch);
      }

      return result;
    }, [alwaysShowHeading, heading, props.allOptions]);
    return <MultiSelectStatic {...props} heading={_heading} />;
  }),
  0
);
export default MultiSelect;

export const DynamicMultiSelect = withDynamicOptionsHOC<any, MultiSelectProps>(MultiSelect);

const DynamicMultiSelectWithAddBase = withAddHOC<
  Omit<MultiSelectProps, "allOptions" | "options"> & WithDynamicOptionsAddedProps<any>
>(DynamicMultiSelect);
export const DynamicMultiSelectWithAdd = ({
  dropdownClassName,
  ...props
}: Omit<MultiSelectProps, "options"> & WithDynamicOptionsAddedProps<any> & WithAddHOCBaseProps) => (
  <DynamicMultiSelectWithAddBase {...props} dropdownClassName={classnames(dropdownClassName, "with_add")} />
);
